import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Sparkles, ArrowRight } from "lucide-react";
import PackageCard from "@/components/PackageCard";
import Seo from "@/components/Seo";
import { getDestination } from "@/lib/api";
import { DESTINATION_IMAGES } from "@/lib/media";

export default function DestinationDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getDestination(slug)
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // getDestination is a stable helper; setData/setLoading are stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const destination = data?.destination;
  const packages = data?.packages || [];

  const breadcrumb = useMemo(
    () =>
      destination
        ? [
            { name: "Home", path: "/" },
            { name: "Destinations", path: "/destinations" },
            { name: destination.name, path: `/destinations/${destination.slug}` },
          ]
        : [],
    [destination],
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center text-slate-500" data-testid="destination-loading">
        Loading…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="destination-not-found">
        <h1 className="font-heading text-3xl text-[#0B192C]">Destination not found</h1>
        <Link to="/destinations" className="btn-primary mt-6">Back to destinations</Link>
      </div>
    );
  }

  const offersLd = {
    "@context": "https://schema.org",
    "@type": "TouristDestination",
    name: destination.name,
    description: destination.overview,
    address: { "@type": "PostalAddress", addressCountry: destination.country },
    includesAttraction: destination.highlights.map((h) => ({
      "@type": "TouristAttraction",
      name: h,
    })),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `${destination.name} ready packages`,
      itemListElement: packages.map((p) => ({
        "@type": "Offer",
        name: p.title,
        description: p.summary,
        priceCurrency: "USD",
        price: p.price_from,
      })),
    },
  };

  return (
    <div data-testid={`destination-page-${destination.slug}`}>
      <Seo
        path={`/destinations/${destination.slug}`}
        title={`${destination.name} DMC — 3N / 4N / 5N Packages by Trawelldmc`}
        description={`${destination.tagline}. ${destination.overview} Ready 3N / 4N / 5N packages from $${packages[0]?.price_from || ""} per pax.`}
        breadcrumb={breadcrumb}
        extraJsonLd={offersLd}
      />
      <section className="relative h-[60vh] min-h-[480px] overflow-hidden">
        <img
          src={DESTINATION_IMAGES[destination.slug] || destination.hero_image}
          alt={`${destination.name} ${destination.country} — Trawelldmc DMC packages and itineraries`}
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(11,25,44,0.35) 0%, rgba(11,25,44,0.85) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-full flex flex-col justify-end pb-16 text-white">
          <Link
            to="/destinations"
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-8"
            data-testid="back-to-destinations"
          >
            <ArrowLeft className="h-4 w-4" /> All destinations
          </Link>
          <div className="overline !text-white/70 mb-4 flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5" /> {destination.country}
          </div>
          <h1 className="font-heading text-5xl md:text-7xl font-light tracking-tight">
            {destination.name}
          </h1>
          <p className="text-white/80 mt-4 text-lg max-w-xl">{destination.tagline}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="overline mb-3">Overview</div>
          <p className="text-slate-700 text-lg leading-relaxed">{destination.overview}</p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {destination.highlights.map((h) => (
              <div key={h} className="flex items-center gap-3 border-l-2 border-[#FF5238] pl-4 py-2">
                <Sparkles className="h-4 w-4 text-[#FF5238]" />
                <span className="text-sm text-[#0B192C] font-medium">{h}</span>
              </div>
            ))}
          </div>
        </div>
        <aside className="lg:col-span-5 bg-[#FDFCF8] border border-slate-200 p-8">
          <div className="overline mb-3">Best For</div>
          <ul className="space-y-3">
            {destination.best_for.map((b) => (
              <li key={b} className="flex items-center gap-3 text-[#0B192C]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#FF5238]" />
                <span className="font-heading text-lg font-medium">{b}</span>
              </li>
            ))}
          </ul>
          <Link
            to={`/inquiry?destination=${destination.slug}`}
            className="btn-primary mt-8 w-full justify-center"
            data-testid="destination-inquiry-cta"
          >
            Request custom proposal <ArrowRight className="h-4 w-4" />
          </Link>
        </aside>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-32">
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="overline mb-3">Ready Packages</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Choose your nights. <br />We handle the rest.
            </h2>
          </div>
          <p className="text-slate-600 max-w-md">
            All packages are fully customizable. Prices are indicative and based on per-pax twin-sharing
            occupancy for groups of 10+. Valid on select dates.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <PackageCard key={pkg.id} pkg={pkg} destinationName={destination.name} />
          ))}
        </div>
      </section>
    </div>
  );
}
